import { useEffect, useRef, useState, useCallback, useLayoutEffect, useMemo } from "react";
import ResizeObserver from "resize-observer-polyfill";
import { useKeydown as useKeydown$1 } from "@ardoq/hooks";
import { isMac } from "@ardoq/common-helpers";
const useKeydown = (effect) => {
  useEffect(() => {
    document.addEventListener("keydown", effect);
    return () => document.removeEventListener("keydown", effect);
  }, [effect]);
};
const useOnMount = (effect) => {
  useEffect(effect, []);
};
const useEffectOnce = (effect) => {
  const destroyFunc = useRef(null);
  const calledOnce = useRef(false);
  const renderAfterCalled = useRef(false);
  if (calledOnce.current) {
    renderAfterCalled.current = true;
  }
  useEffect(() => {
    if (calledOnce.current) {
      return;
    }
    calledOnce.current = true;
    destroyFunc.current = effect();
    return () => {
      if (!renderAfterCalled.current) {
        return;
      }
      if (typeof destroyFunc.current === "function") {
        destroyFunc.current();
      }
    };
  }, []);
};
const useIsMounted = () => {
  const [isMounted, setIsMounted] = useState(false);
  useEffectOnce(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  });
  return isMounted;
};
function useEvent(eventName, options) {
  const [event, setEvent] = useState(null);
  useOnMount(() => {
    window.addEventListener(eventName, setEvent, options);
    return () => window.removeEventListener(eventName, setEvent, options);
  });
  return event;
}
function getWindowSize() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height
  };
}
function useWindowSize() {
  const [windowSize, setWindowSize] = useState(getWindowSize());
  useEffect(() => {
    function handleResize() {
      setWindowSize(getWindowSize());
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return windowSize;
}
const usePrevious = (value) => {
  const ref = useRef(void 0);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};
function usePreviousDefined(value) {
  const ref = useRef(void 0);
  useEffect(() => {
    if (value !== void 0) {
      ref.current = value;
    }
  });
  return value !== void 0 ? value : ref.current;
}
const useResizeObserver = (ref, callback) => {
  const [width, setWidth] = useState();
  const [height, setHeight] = useState();
  const handleResize = useCallback(
    (entries) => {
      if (!Array.isArray(entries)) return;
      const entry = entries[0];
      setWidth(entry.contentRect.width);
      setHeight(entry.contentRect.height);
      callback == null ? void 0 : callback(entry.contentRect);
    },
    [callback]
  );
  useLayoutEffect(() => {
    if (!ref.current) return;
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(ref.current);
    return () => resizeObserver == null ? void 0 : resizeObserver.disconnect();
  }, [ref, handleResize]);
  return { width, height };
};
const isClickWithinElement = (element, event) => {
  const { clientX, clientY } = event;
  const { left, top, right, bottom } = element.getBoundingClientRect();
  return clientX >= left && clientX <= right && clientY >= top && clientY <= bottom;
};
const useOnClickOutside = (ref, onClickOutside) => {
  const syntheticEventRef = useRef(false);
  useEffect(() => {
    const currentElement = ref.current;
    const onCurrentElementClick = () => {
      syntheticEventRef.current = true;
    };
    const clickListener = (event) => {
      const insideReactTree = syntheticEventRef.current;
      syntheticEventRef.current = false;
      if (ref.current && !ref.current.contains(event.target) && // If the click is over the ref element position, don't call onClickOutside
      // This can happen when element rendered via portal is clicked
      !isClickWithinElement(ref.current, event) && !insideReactTree) {
        onClickOutside();
      }
    };
    currentElement == null ? void 0 : currentElement.addEventListener("click", onCurrentElementClick);
    document.addEventListener("click", clickListener);
    return () => {
      currentElement == null ? void 0 : currentElement.removeEventListener("click", onCurrentElementClick);
      document.removeEventListener("click", clickListener);
    };
  }, [ref, onClickOutside]);
};
function useMedia(query) {
  const [matches, setMatches] = useState(window.matchMedia(query).matches);
  useEffect(() => {
    const mediaQueryList = window.matchMedia(query);
    setMatches(mediaQueryList.matches);
    const listener = () => {
      setMatches(mediaQueryList.matches);
    };
    mediaQueryList.addEventListener("change", listener);
    return () => {
      mediaQueryList.removeEventListener("change", listener);
    };
  }, [query]);
  return matches;
}
const useMouseOver = (onSelect) => {
  const mouseOverRef = useRef(false);
  const handleMouseMove = useCallback(() => {
    if (!mouseOverRef.current) {
      mouseOverRef.current = true;
      onSelect();
    }
  }, [onSelect]);
  const handleMouseLeave = useCallback(() => {
    mouseOverRef.current = false;
  }, []);
  return { onMouseMove: handleMouseMove, onMouseLeave: handleMouseLeave };
};
const scrollSelectedResultIntoView = (selectedIndex, rowRefs, container) => {
  if (selectedIndex === void 0 || selectedIndex === null) return;
  const node = rowRefs[selectedIndex];
  if (!node || !container) return;
  const nodeRect = node.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  if (nodeRect.top < containerRect.top || nodeRect.bottom > containerRect.bottom) {
    node.scrollIntoView({ block: "nearest" });
  }
};
const useListAutoScroll = (selectedIndex) => {
  const containerRef = useRef(null);
  const rowRefs = useRef([]);
  useEffect(() => {
    scrollSelectedResultIntoView(
      selectedIndex,
      rowRefs.current,
      containerRef.current
    );
  }, [selectedIndex]);
  return { containerRef, rowRefs };
};
const useQuickSearchShortcut = (action) => {
  useKeydown$1((event) => {
    const metaKey = isMac() ? event.metaKey : event.ctrlKey;
    if (metaKey && event.key === "k") {
      event.preventDefault();
      action();
    }
  });
};
const SHOW_CHEVRON_OFFSET = 8;
const CHEVRON_WIDTH = 24;
const useIntersectionObserver = (elementRef, {
  threshold = 0,
  root = null,
  rootMargin = "0%",
  freezeOnceVisible = false
} = {}) => {
  const [entry, setEntry] = useState();
  const frozen = (entry == null ? void 0 : entry.isIntersecting) && freezeOnceVisible;
  const updateEntry = ([entry2]) => {
    setEntry(entry2);
  };
  useEffect(() => {
    const node = elementRef == null ? void 0 : elementRef.current;
    const hasIOSupport = !!window.IntersectionObserver;
    if (!hasIOSupport || frozen || !node) return;
    const observerParams = { threshold, root, rootMargin };
    const observer = new IntersectionObserver(updateEntry, observerParams);
    observer.observe(node);
    return () => observer.disconnect();
  }, [elementRef, JSON.stringify(threshold), root, rootMargin, frozen]);
  return entry;
};
const getScrollTarget = (wrapperEl, direction) => {
  const wrapperElEdgePosition = wrapperEl.getBoundingClientRect()[direction];
  const children = wrapperEl.children;
  const scrollTargetIndex = Array.from(children).findIndex((child2) => {
    const childEdgePosition = child2.getBoundingClientRect()[direction];
    return childEdgePosition > wrapperElEdgePosition + (direction === "left" ? CHEVRON_WIDTH : -24);
  });
  const child = children[scrollTargetIndex > 0 ? scrollTargetIndex - 1 : 0];
  return child instanceof HTMLElement ? child : null;
};
const scrollWrapperToPosition = (wrapperEl, position) => wrapperEl.scrollTo({ left: position, behavior: "smooth" });
const isScrollPossible = (wrapperEl) => Boolean(wrapperEl) && wrapperEl instanceof HTMLElement;
const getScrollLeftPosition = (scrollTarget, wrapperWidth) => scrollTarget.offsetLeft - (wrapperWidth - scrollTarget.offsetWidth - 2 * CHEVRON_WIDTH);
const getScrollRightPosition = (scrollTarget) => scrollTarget.offsetLeft + CHEVRON_WIDTH;
const onChevronClick = (wrapperEl, wrapperWidth, direction) => {
  const scrollTarget = getScrollTarget(wrapperEl, direction);
  if (scrollTarget) {
    scrollWrapperToPosition(
      wrapperEl,
      direction === "left" ? getScrollLeftPosition(scrollTarget, wrapperWidth) : getScrollRightPosition(scrollTarget)
    );
  }
};
const useChevronSliders = (wrapperWidth, children) => {
  const wrapperRef = useRef(null);
  const entry = useIntersectionObserver(wrapperRef);
  const isVisible = !!(entry == null ? void 0 : entry.isIntersecting);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const isLeftChevronVisible = useMemo(
    () => scrollLeft > SHOW_CHEVRON_OFFSET,
    [scrollLeft]
  );
  const isRightChevronVisible = useMemo(
    () => scrollLeft + wrapperWidth < contentWidth - SHOW_CHEVRON_OFFSET,
    [scrollLeft, wrapperWidth, contentWidth]
  );
  const wrapperOnScroll = useCallback(() => {
    var _a;
    const wrapperScrollLeft = (_a = wrapperRef.current) == null ? void 0 : _a.scrollLeft;
    if (wrapperScrollLeft === void 0) return;
    setScrollLeft(wrapperScrollLeft);
  }, [wrapperRef]);
  const getOnChevronClick = useCallback(
    (direction) => () => {
      if (isScrollPossible(wrapperRef.current)) {
        onChevronClick(wrapperRef.current, wrapperWidth, direction);
      }
    },
    [wrapperRef, wrapperWidth]
  );
  useEffectOnce(() => {
    if (!wrapperRef.current) return;
    setContentWidth(wrapperRef.current.scrollWidth);
  });
  useEffect(() => {
    if (isVisible) {
      if (!wrapperRef.current) return;
      setContentWidth(wrapperRef.current.scrollWidth);
    }
  }, [isVisible, children]);
  return {
    wrapperRef,
    isLeftChevronVisible,
    isRightChevronVisible,
    onLeftChevronClick: getOnChevronClick(
      "left"
      /* LEFT */
    ),
    onRightChevronClick: getOnChevronClick(
      "right"
      /* RIGHT */
    ),
    wrapperOnScroll
  };
};
const MARGIN = 4;
const isLeftPlacementPossible = (elementToBePlacedBox, referenceBox) => {
  return window.innerWidth > referenceBox.left + elementToBePlacedBox.width;
};
const isBottomPlacementPossible = (elementToBePlacedBox, referenceBox) => {
  return window.innerHeight > referenceBox.bottom + elementToBePlacedBox.height + MARGIN;
};
const getLeft = (elementToBePlacedBox, referenceBox) => {
  return isLeftPlacementPossible(elementToBePlacedBox, referenceBox) ? referenceBox.left : referenceBox.right - elementToBePlacedBox.width;
};
const getTop = (elementToBePlacedBox, referenceBox) => {
  return isBottomPlacementPossible(elementToBePlacedBox, referenceBox) ? referenceBox.bottom + MARGIN : referenceBox.top - elementToBePlacedBox.height - MARGIN;
};
function getDefaultOffscreenPlacement() {
  return {
    left: -1e3,
    top: -1e3
  };
}
function getRelativePlacementCSS(elementToBePlacedBox, referenceBox) {
  return {
    left: getLeft(elementToBePlacedBox, referenceBox),
    top: getTop(elementToBePlacedBox, referenceBox)
  };
}
export {
  getDefaultOffscreenPlacement,
  getRelativePlacementCSS,
  useChevronSliders,
  useEffectOnce,
  useEvent,
  useIsMounted,
  useKeydown,
  useListAutoScroll,
  useMedia,
  useMouseOver,
  useOnClickOutside,
  useOnMount,
  usePrevious,
  usePreviousDefined,
  useQuickSearchShortcut,
  useResizeObserver,
  useWindowSize
};
//# sourceMappingURL=index.js.map
